import React, { Component } from 'react'
import { array, bool, object } from 'prop-types'
import { createComponent, withTheme, ThemeProvider } from '@ycos/fela'
import { Grid } from '@ycos/component-grid'
import { hasTag } from '@ycos/coremedia-renderer'

const getColumnConfiguration = (layoutVariant) => {
  return {
    'isc-functional-fullbleed': {
      small: {
        offset: 0,
        width: 6
      },
      medium: {
        offset: 0,
        width: 12
      },
      fullBleed: true
    },
    'isc-functional-large': {
      small: {
        offset: 0,
        width: 6
      },
      medium: {
        offset: 0,
        width: 12
      }
    },
    'isc-functional': {
      small: {
        offset: 0,
        width: 6
      },
      medium: {
        offset: 1,
        width: 10
      }
    }
  }[layoutVariant]
}

const FunctionalWrapper = createComponent('FunctionalWrapper', ({ arReverse }) => ({
  display: 'block',
  'screen-medium': {
    display: 'flex',    
    direction: arReverse ? 'rtl': 'initial'
  }
}))

const FunctionalColumn = createComponent('FunctionalColumn', ({ 
    theme, 
    dividerLine, 
    shortDividerLine, 
    dividerDot, 
    brandId, 
    addPaddingBottom,
    addPaddingBottomMobile
  }) => {
  const { spacingMultiplier, 
          custom, 
          rowOffsetTopSmall, 
          rowOffsetTopMedium, 
          rowOffsetTopLarge, 
          rowOffsetTopXLarge,
          rowOffsetBottomSmall, 
          rowOffsetBottomMedium, 
          rowOffsetBottomLarge, 
          rowOffsetBottomXLarge, 
          rightOffset } = theme
  const topSmall = rowOffsetTopSmall * spacingMultiplier
  const topMedium = rowOffsetTopMedium * spacingMultiplier
  const topLarge = rowOffsetTopLarge * spacingMultiplier
  const topXLarge = rowOffsetTopXLarge * spacingMultiplier
  const bottomSmall = addPaddingBottomMobile ? rowOffsetBottomSmall * spacingMultiplier : 0
  const bottomMedium = addPaddingBottom ? rowOffsetBottomMedium * spacingMultiplier : 0
  const bottomLarge = addPaddingBottom ? rowOffsetBottomLarge * spacingMultiplier : 0
  const bottomXLarge = addPaddingBottom ? rowOffsetBottomXLarge * spacingMultiplier : 0
  const gutter = 3 * spacingMultiplier
  const gutterMRP = rightOffset * spacingMultiplier
  const hasDivider = shortDividerLine || dividerLine || dividerDot
  const dividerHeight = (paddingTop) => (shortDividerLine ? `calc(33% - ${paddingTop}px / 3)` : `calc(100% - ${paddingTop}px)`)

  return {
    display: 'inline-block',
    width: '100%',
    paddingTop: `${topSmall}px`,
    paddingBottom: `${bottomSmall}px`,
    'screen-medium': {
      width: '100%',
      marginRight: brandId === 'mrp' ? gutterMRP: gutter,
      paddingTop: `${topMedium}px`,
      paddingBottom: `${bottomMedium}px`,
      position: hasDivider ? 'relative' : 'static',
      ':last-child': {
        marginRight: 0
      },
      ':after': hasDivider
        ? {
            position: 'absolute',
            content: '" "',
            height: dividerHeight(topMedium),
            bottom: shortDividerLine ? dividerHeight(topMedium) : '0',
            right: brandId === 'mrp' ? `-${gutterMRP / 2}px`:`-${gutter / 2}px`,
            width: '1px',
            background: dividerDot ? undefined : custom.divider.color,
            backgroundImage: dividerDot ? `linear-gradient(${theme.typography.colors.SecondaryGreySteel} 25%, rgba(255,255,255,0) 0%)` : undefined,
            backgroundSize: dividerDot ? '1px 4px' : undefined,
            backgroundRepeat: dividerDot ? 'repeat-y' : undefined,
            backgroundPosition: dividerDot ? 'right' : undefined
          }
        : undefined,
      ':last-child:after': hasDivider
        ? {
            content: 'none'
          }
        : undefined
    },
    'screen-large': {
      paddingTop: `${topLarge}px`,
      paddingBottom: `${bottomLarge}px`,
      ':after': hasDivider
        ? {
            height: dividerHeight(topLarge),
            bottom: shortDividerLine ? dividerHeight(topLarge) : '0'
          }
        : undefined
    },
    'screen-xlarge': {
      paddingTop: `${topXLarge}px`,
      paddingBottom: `${bottomXLarge}px`,
      ':after': hasDivider
        ? {
            height: dividerHeight(topXLarge),
            bottom: shortDividerLine ? dividerHeight(topXLarge) : '0'
          }
        : undefined
    }
  }
})

class Functional extends Component {
  static layoutVariant = ['isc-functional', 'isc-functional-large', 'isc-functional-fullbleed']

  render() {
    const { item, theme, brand } = this.props
    const { layoutVariant = 'isc-functional' } = item
    const columnConfiguration = getColumnConfiguration(layoutVariant)
    const children = Array.isArray(this.props.children) ? this.props.children : [this.props.children]
    const dividerLine = this.props.dividerLine || hasTag(item.tags, 'Columns.DividerLine')
    const shortDividerLine = this.props.shortDividerLine || hasTag(item.tags, 'Columns.ShortDividerLine')
    const dividerDot = hasTag(item.tags, 'Columns.DividerDot') || this.props.dividerDot
    const arReverse = this.props.arReverse || hasTag(item.tags, 'Columns.ArReverse')
    const addBottomPadding = this.props.addPaddingBottom || hasTag(item && item.tags, 'Columns.AddBottomPadding')
    const addBottomPaddingMobile = this.props.addPaddingBottomMobile || hasTag(item && item.tags, 'Columns.AddBottomPaddingMobile')

    return (
      <ThemeProvider theme={{ ...theme, direction: 'ltr' }}>
        <Grid.Row fullBleed={columnConfiguration.fullBleed || false}>
          <Grid.Span small={{ ...columnConfiguration.small }} medium={{ ...columnConfiguration.medium }} large={{ ...columnConfiguration.large }} fullBleed={columnConfiguration.fullBleed || false}>
            <FunctionalWrapper arReverse={arReverse}>
              {children.map(
                (child, idx) =>
                  idx <= 10 && (
                    <FunctionalColumn 
                      key={child.key} 
                      dividerLine={dividerLine} 
                      shortDividerLine={shortDividerLine} 
                      dividerDot={dividerDot} 
                      brandId={brand?.brandId} 
                      addPaddingBottom={addBottomPadding}
                      addPaddingBottomMobile={addBottomPaddingMobile}
                    >
                      <ThemeProvider theme={theme}>
                        {child}
                      </ThemeProvider>
                    </FunctionalColumn>
                  )
              )}
            </FunctionalWrapper>
          </Grid.Span>
        </Grid.Row>
      </ThemeProvider>
    )
  }
}

Functional.propTypes = {
  children: array,
  item: object,
  dividerLine: bool,
  arReverse: bool,
  addPaddingBottom: bool,
  addPaddingBottomMobile: bool
}

Functional.defaultProps = {
  children: [],
  item: {},
  dividerLine: false,
  arReverse: false,
  addPaddingBottom: false,
  addPaddingBottomMobile: false
}

export default withTheme(Functional)
